import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import '../csscomponent/Services.css';
import { packagePlans } from '../data/packagePlans';
import { getCmsServices, getCmsSettings, saveCartItem } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';
import { useAuth } from '../lib/authSession';
import AdMobBanner from './AdMobBanner';

let cachedLeftAnim = null;
let cachedRightAnim = null;

function Services({ isHomeSection = false }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [animations, setAnimations] = useState({ left: cachedLeftAnim, right: cachedRightAnim });
  const [offerings, setOfferings] = useState([]);
  const [cartMessage, setCartMessage] = useState('');
  const [intro, setIntro] = useState({
    title: 'Services That Move Your Business Forward',
    description: 'From discovery to launch, we build products and websites that are fast, usable, and ready for scale.',
  });

  useEffect(() => {
    let mounted = true;
    if (cachedLeftAnim && cachedRightAnim) {
      return undefined;
    }
    Promise.all([import('../animationservices.json'), import('../login.json')])
      .then(([left, right]) => {
        cachedLeftAnim = left.default;
        cachedRightAnim = right.default;
        if (mounted) {
          setAnimations({ left: left.default, right: right.default });
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [servicesRes, settingsRes] = await Promise.all([
          getCmsServices(),
          getCmsSettings({ keys: ['services_intro'] }),
        ]);
        if (!mounted) return;
        setOfferings(Array.isArray(servicesRes?.items) ? servicesRes.items.map((item) => ({
          title: item.title,
          text: item.description,
          icon: item.icon,
        })) : []);
        const content = settingsRes?.settings?.services_intro || {};
        setIntro((prev) => ({
          title: content.title || prev.title,
          description: content.description || prev.description,
        }));
      } catch (_) {}
    };
    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'services'].includes(payload.scope)) {
        load();
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const visiblePackages = isHomeSection ? packagePlans.slice(0, 3) : packagePlans;

  const handleAddToCart = async (pack) => {
    setCartMessage('');
    try {
      await saveCartItem({
        packageId: pack.id,
        packageName: pack.name,
        priceInr: pack.price,
        quantity: 1,
      });
      window.dispatchEvent(new CustomEvent('sd:cart-updated'));
      toast.success(`${pack.name} added to cart!`);
      setCartMessage(`${pack.name} package added to cart successfully. Redirecting to cart...`);

      if (!isAuthenticated) {
        sessionStorage.setItem('pendingCheckoutPath', '/dashboard/cart');
        navigate('/login');
        return;
      }

      navigate('/dashboard/cart', {
        state: {
          cartNotice: `${pack.name} package added to cart successfully.`,
        },
      });
    } catch (error) {
      toast.error('Unable to add this package to cart.');
      setCartMessage(error?.message || 'Unable to add this package to cart right now.');
    }
  };

  return (
    <section className={`services ${isHomeSection ? 'home-services' : 'page-services'}`} id="services">
      <div className="services-head">
       <div className="services-head-lottie left">
          {animations.left ? <Lottie animationData={animations.left} loop /> : null}
        </div>
        <div className="services-head-copy">
          <h2 className="section-title services-main-title">{intro.title}</h2>
          <p>{intro.description}</p>
        </div>
        <div className="services-head-lottie right">
          {animations.right ? <Lottie animationData={animations.right} loop /> : null}
        </div>
      </div>

      <div className="service-list">
        {offerings.map((service, index) => (
          <motion.article
            key={service.title}
            className="service-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.1 }}
          >
            <h3>{service.title}</h3>
            <p>{service.text}</p>
          </motion.article>
        ))}
      </div>

      <h2 id="packages" className="section-title packages-title">Website Development Packages</h2>
      {cartMessage ? <p className="services-cart-message">{cartMessage}</p> : null}
      <div className="package-list">
        {visiblePackages.map((pack, index) => (
          <motion.article
            key={pack.name}
            className={`package-card ${pack.popular ? 'popular' : ''}`}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.12 }}
          >
            <h3>{pack.name} Package</h3>
            <span className="package-note">{pack.billingNote}</span>
            <ul>
              {pack.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <p className="package-timeline">{pack.timeline}</p>
            <p className="package-price">INR {pack.price.toLocaleString()}</p>
            <div className="package-action-row">
              <Link to={`/packages/${pack.id}`} className="btn filled package-btn">
                {pack.cta}
              </Link>
              <button type="button" className="btn outline package-btn package-cart-btn" onClick={() => handleAddToCart(pack)}>
                Add to Cart
              </button>
            </div>
          </motion.article>
        ))}
      </div>

      <AdMobBanner title="Featured Services" subtitle="Explore verified solutions & packages" />

      {!isHomeSection && (
        <div className="services-cta-row">
          <Link to="/career" className="services-link">Work with our team</Link>
          <Link to="/product" className="services-link">Explore recent products</Link>
        </div>
      )}
    </section>
  );
}

export default Services;
